import { Component, DestroyRef, effect, inject, OnInit, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ProductConfigStepperBarComponent } from "@components/stepper/product-config-stepper-bar/product-config-stepper-bar.component";
import { Device, DeviceType } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStep, StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { MonitoringProject as Project } from "@domain/project/monitoring-project";
import { CentralStationsSelectorType } from "@domain/project/monitoring-selector-types.enum";
import { WizardComponent } from "@odx/angular/components/wizard";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import {
  appendDeviceTypes,
  buildDeviceTypeMap,
  CentralStationOptionalOptionBuckets,
  resolveOptionalOptionBuckets,
} from "@project/monitoring/central-station-configuration/central-station-configuration.utils";
import { CentralStationLicenceSelectorComponent } from "@project/monitoring/central-station-configuration/central-station-licence-selector/central-station-licence-selector.component";
import { CentralStationOptionSelectorComponent } from "@project/monitoring/central-station-configuration/central-station-option-selector/central-station-option-selector.component";
import {
  buildCentralStationGroupedSourceItems,
  CentralStationGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getCentralStationGroupedPartLabels,
  getMonitoringOtherProductsLabel,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartsService } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.service";
import {
  MonitoringGroupedPartSection,
  MonitoringGroupedPartSourceItem,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { MonitoringOverviewComponent } from "@project/monitoring/grouped-parts/monitoring-overview.component";
import { MonitoringConfigurationFactory } from "@project/monitoring/monitoring-configuration.factory";
import {
  createFamilyVariantStep,
  getConfigurationOptionIdentifier,
  getSelectedFamilyOption,
} from "@project/monitoring/monitoring-configuration.utils";
import { FamilyVariantMode, isCentralStationFamilySteps } from "@project/monitoring/monitoring-family-wizard.utils";
import {
  OptionDisplayWithQuantity,
  OptionSelectorComponent,
} from "@project/monitoring/option-device-selector/option-selector.component";
import { OptionDisplay } from "@project/monitoring/option-display.service";
import { ProductFamilySelectorComponent } from "@project/monitoring/product-family-selector/product-family-selector.component";
import { catchError, filter, forkJoin, map, Observable, of, Subject, switchMap } from "rxjs";

@Component({
  selector: "app-central-station-configuration",
  imports: [
    UiKitModule,
    ConfigurationWizardBackButtonComponent,
    WizardComponent,
    ProductConfigStepperBarComponent,
    CentralStationLicenceSelectorComponent,
    CentralStationOptionSelectorComponent,
    MonitoringOverviewComponent,
    ProductFamilySelectorComponent,
    OptionSelectorComponent,
  ],
  templateUrl: "./central-station-configuration.component.html",
  styleUrl: "./central-station-configuration.component.scss",
})
export class CentralStationConfigurationComponent implements OnInit {
  protected readonly CentralStationsSelectorType = CentralStationsSelectorType;
  protected readonly CentralStationsSelectorTitleKeyMap: Record<CentralStationsSelectorType, string> = {
    [CentralStationsSelectorType.FAMILY]: $localize`:@@centralStationConfiguration.step0.title:Select family`,
    [CentralStationsSelectorType.LICENCE]: $localize`:@@centralStationConfiguration.step1.title:Select licence`,
    [CentralStationsSelectorType.ADDITIONAL_LICENCES]: $localize`:@@centralStationConfiguration.step2.title:Additional licences`,
    [CentralStationsSelectorType.ACCESSORIES]: $localize`:@@centralStationConfiguration.step3.title:Accessories`,
    [CentralStationsSelectorType.OVERVIEW]: $localize`:@@centralStationConfiguration.summary.title:Central station configuration summary`,
  };
  protected readonly overviewCategoryLabels: CentralStationGroupedPartLabels = getCentralStationGroupedPartLabels();
  protected readonly overviewCategoryLabelMap: Partial<Record<CentralStationsSelectorType, string>> = {
    [CentralStationsSelectorType.FAMILY]: this.overviewCategoryLabels.base,
    [CentralStationsSelectorType.LICENCE]: this.overviewCategoryLabels.licence,
    [CentralStationsSelectorType.ADDITIONAL_LICENCES]: this.overviewCategoryLabels.additional,
    [CentralStationsSelectorType.ACCESSORIES]: this.overviewCategoryLabels.accessory,
  };

  private activeRoute = inject(ActivatedRoute);
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);
  private readonly destroyRef = inject(DestroyRef);
  private monitoringGroupedPartsService = inject(MonitoringGroupedPartsService);
  private readonly loadCountry = signal<string | undefined | null>(null);
  protected project!: Project;
  protected originalConfiguration?: CentralStationConfiguration;
  protected activeStep = 0;

  protected family?: Family;
  protected familyVariantStep?: FamilyStep;
  protected licenceStep?: FamilyStep;
  protected optionalStep?: FamilyStep;
  protected accessoriesStep?: FamilyStep;

  protected licenceForm!: FormGroup;
  protected selectedFamilyForm!: FormGroup;

  protected selectedLicence?: OptionDisplay;
  protected selectedFamily?: OptionDisplay;
  protected selectedFamilyVariant?: OptionDisplay;
  protected selectedAdditionalLicences: MonitoringProductItem[] = [];
  protected selectedAccessories: MonitoringProductItem[] = [];

  protected groupedSections: MonitoringGroupedPartSection[] = [];
  protected readonly otherProductsLabel = getMonitoringOtherProductsLabel();

  protected additionalLicenceOptions: FamilyStepOption[] = [];
  protected accessoryOptions: FamilyStepOption[] = [];
  protected originalFamilyVariantOptions: MonitoringProductItem[] = [];
  protected selectedFamilyVariantForm!: FormGroup;
  private optionalOptionsResolved = false;

  private deviceTypesByMatNr = new Map<string, DeviceType>();
  private discontinuedDeviceMatNrs = new Set<string>();
  private pick0Steps: FamilyStep[] = [];
  private readonly additionalLicenceTypes = [DeviceType.CS_LICENCE_M300, DeviceType.CS_LICENCE];
  private readonly accessoryTypes = [DeviceType.CS_A];
  private filterCountry?: string;
  private hasRefreshedDeviceCache = false;
  private readonly overviewItems$ = new Subject<MonitoringGroupedPartSourceItem[]>();

  protected readonly MonitoringCategory = MonitoringCategory;
  protected readonly FloorplanMenuType = FloorplanMenuType;
  protected readonly accessoriesSingleHeader = $localize`:@@centralStationConfiguration.accessories.singleHeader:Einzelzubehör`;
  protected readonly accessoriesQuantityHeader = $localize`:@@centralStationConfiguration.accessories.quantityHeader:Mengenzubehör`;

  @ViewChild("configurationWizard")
  private configurationWizard!: WizardComponent;

  @ViewChild("backButton")
  private backButton!: ConfigurationWizardBackButtonComponent;

  private readonly centralStationData = toSignal(
    toObservable(this.loadCountry).pipe(
      filter((country): country is string | undefined => country !== null),
      switchMap((filterCountry) =>
        this.monitoringProductService.init().pipe(
          switchMap(() => this.loadCentralStationCategoryData(filterCountry)),
          switchMap(({ families, devices }) => this.ensureValidCentralStationData(families, devices, filterCountry)),
          catchError((err) => {
            console.error("Failed loading central station data", err);
            return of({ families: [], devices: [] });
          }),
        ),
      ),
    ),
    { initialValue: { families: [], devices: [] } },
  );
  constructor() {
    effect(() => this.syncCentralStationData());

    this.overviewItems$
      .pipe(
        switchMap((items) => {
          if (!items.length) return of<MonitoringGroupedPartSection[]>([]);

          return this.monitoringGroupedPartsService.buildSections(
            items,
            MonitoringCategory.CENTRAL_STATIONS,
            this.otherProductsLabel,
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sections) => {
        this.groupedSections = sections;
      });
  }

  ngOnInit(): void {
    this.project = this.activeRoute.parent!.snapshot.data["project"];

    if (this.activeRoute.snapshot.queryParams["config_id"]) {
      const configId = this.activeRoute.snapshot.queryParams["config_id"];
      this.originalConfiguration = this.project.centralStations.find((station) => station.id === configId);
    }

    this.loadCentralStationData();
  }

  protected setSelectedFamily($event: OptionDisplay | undefined) {
    this.selectedFamily = $event;
    this.family = $event?.family;
    const familyVariant = $event?.familyVariant?.variant;
    this.familyVariantStep =
      familyVariant?.mode === FamilyVariantMode.MULTIPLE_OPTIONS ? createFamilyVariantStep(familyVariant.step) : undefined;
    const normalizedSteps = isCentralStationFamilySteps($event?.familyVariant?.steps) ? $event.familyVariant.steps : undefined;
    const licenceStep = normalizedSteps?.licenceStep ?? this.family?.steps?.[1];
    const licenceSelectionType = licenceStep
      ? (Object.values(StepSelectionType).find((type) => type === licenceStep.stepSelectionType) ?? StepSelectionType.PICK1)
      : undefined;
    this.licenceStep = licenceStep
      ? new FamilyStep(this.filterAvailableOptions(licenceStep.options ?? []), licenceSelectionType ?? StepSelectionType.PICK1)
      : undefined;
    this.accessoriesStep = normalizedSteps?.accessoriesStep;
    this.selectedFamilyVariant =
      familyVariant?.mode === FamilyVariantMode.SINGLE_OPTION && $event ? { ...$event, option: familyVariant.option } : undefined;

    if (this.originalConfiguration && $event?.family?.familyNumber === this.originalConfiguration.familyNumber) {
      this.originalFamilyVariantOptions = this.originalConfiguration.family ? [this.originalConfiguration.family] : [];
    } else {
      this.originalFamilyVariantOptions = [];
    }

    this.pick0Steps = this.family?.steps.filter((step) => step.stepSelectionType === StepSelectionType.PICK0TON) ?? [];
    this.optionalStep = this.pick0Steps[0];
    this.loadOptionalDeviceTypes();
    this.refreshOverviewData();
  }

  protected setSelectedFamilyVariant($event: OptionDisplayWithQuantity[]) {
    this.selectedFamilyVariant = $event[0]?.optionDisplay;
    this.refreshOverviewData();
  }

  protected setSelectedLicence($event: OptionDisplay) {
    this.selectedLicence = $event;
    this.refreshOverviewData();
  }

  protected updateAdditionalLicences(items: MonitoringProductItem[]) {
    this.selectedAdditionalLicences = items ?? [];
    this.refreshOverviewData();
  }

  protected updateAccessories(items: MonitoringProductItem[]) {
    this.selectedAccessories = items ?? [];
    this.refreshOverviewData();
  }

  protected setActiveStep(event: number) {
    this.activeStep = event;
  }

  protected onSubmit() {
    if (this.activeStep === 0) {
      this.selectedFamilyForm.markAllAsTouched();
      if (!this.selectedFamilyForm.valid) return;
    }

    if (this.activeStep === this.familyVariantStepIndex) {
      this.selectedFamilyVariantForm.markAllAsTouched();
      if (!this.selectedFamilyVariantForm.valid) return;
    }

    if (this.activeStep === this.licenceStepIndex) {
      if (!this.validateLicenceStep()) return;
    }

    if (
      this.activeStep === this.additionalLicencesStepIndex &&
      !this.isSelectionValid(this.additionalLicenceOptions, this.selectedAdditionalLicences)
    ) {
      return;
    }

    if (
      this.activeStep === this.accessoriesStepIndex &&
      !this.isSelectionValid(this.accessoryOptions, this.selectedAccessories)
    ) {
      return;
    }

    if (this.activeStep === this.summaryStepIndex) {
      this.persistConfiguration();
      return;
    }

    this.configurationWizard.setActiveStepValid();
    this.configurationWizard.nextStep();
  }

  protected onPrevious() {
    this.configurationWizard.previousStep();
  }

  protected get familyVariantStepIndex(): number {
    return this.hasFamilyVariantStep ? 1 : -1;
  }

  protected get licenceStepIndex(): number {
    return this.hasLicenceStep ? 1 + (this.hasFamilyVariantStep ? 1 : 0) : -1;
  }

  protected get additionalLicencesStepIndex(): number {
    return this.hasAdditionalLicencesStep ? 1 + (this.hasFamilyVariantStep ? 1 : 0) + (this.hasLicenceStep ? 1 : 0) : -1;
  }

  protected get summaryStepIndex(): number {
    return (
      1 +
      (this.hasFamilyVariantStep ? 1 : 0) +
      (this.hasLicenceStep ? 1 : 0) +
      (this.hasAdditionalLicencesStep ? 1 : 0) +
      (this.hasAccessoriesStep ? 1 : 0)
    );
  }

  protected get steps(): number {
    return this.summaryStepIndex + 1;
  }

  protected get hasFamilyVariantStep(): boolean {
    return !!this.familyVariantStep?.options?.length;
  }

  protected get hasLicenceStep(): boolean {
    return this.family ? !!this.licenceStep?.options?.length : true;
  }

  protected get hasAdditionalLicencesStep(): boolean {
    return this.family ? this.additionalLicenceOptions.length > 0 : true;
  }

  protected get accessoriesStepIndex(): number {
    return this.hasAccessoriesStep
      ? 1 + (this.hasFamilyVariantStep ? 1 : 0) + (this.hasLicenceStep ? 1 : 0) + (this.hasAdditionalLicencesStep ? 1 : 0)
      : -1;
  }

  protected get hasAccessoriesStep(): boolean {
    return this.family ? this.accessoryOptions.length > 0 : true;
  }

  private loadCentralStationData() {
    const filterCountry = this.productService.getFilterCountry();
    this.filterCountry = filterCountry;
    this.loadCountry.set(filterCountry);
  }

  private syncCentralStationData() {
    if (this.loadCountry() === null) return;
    const { families, devices } = this.centralStationData();
    this.initializeConfiguration(families, devices);
  }

  private validateLicenceStep() {
    if (!this.licenceForm) return false;

    this.licenceForm.markAllAsTouched();

    return this.licenceForm.valid;
  }

  private persistConfiguration() {
    if (this.originalConfiguration) {
      this.updateCentralStationConfig(this.originalConfiguration);
    } else {
      this.createCentralStationConfig();
    }
  }

  private loadCentralStationCategoryData(filterCountry?: string) {
    return forkJoin({
      families: this.loadWithFallback(
        (country) => this.monitoringProductService.loadCategoryFamiliesFromDB(MonitoringCategory.CENTRAL_STATIONS, country),
        filterCountry,
      ),
      devices: this.loadWithFallback(
        (country) => this.monitoringProductService.loadAllCategoryDevicesFromDB(MonitoringCategory.CENTRAL_STATIONS, country),
        filterCountry,
      ),
    });
  }

  private loadWithFallback<T>(loader: (country?: string) => Observable<T[]>, filterCountry?: string) {
    return loader(filterCountry).pipe(
      switchMap((items) => {
        if (!filterCountry || items.length > 0) return of(items);
        return loader(undefined);
      }),
    );
  }

  private ensureValidCentralStationData(families: Family[], devices: Device[], filterCountry?: string) {
    if (this.hasRefreshedDeviceCache) return of({ families, devices });
    if (!this.shouldRefreshDevices(devices) || !navigator.onLine) return of({ families, devices });

    this.hasRefreshedDeviceCache = true;
    return this.monitoringProductService.refreshAll().pipe(switchMap(() => this.loadCentralStationCategoryData(filterCountry)));
  }

  private shouldRefreshDevices(devices: Device[]) {
    if (devices.length === 0) return true;

    return !devices.some((device) => Object.values(DeviceType).includes(device.deviceType as DeviceType));
  }

  private initializeConfiguration(families: Family[], devices: Device[]) {
    this.family = this.selectFamily(families);
    if (!this.family) return;

    this.assignFamilySteps(this.family);
    this.deviceTypesByMatNr = buildDeviceTypeMap(devices);
    this.discontinuedDeviceMatNrs = new Set(
      devices.filter((device) => device.isAvailable === false).map((device) => String(device.matNr)),
    );
    this.loadOptionalDeviceTypes();
  }

  private assignFamilySteps(family: Family) {
    const steps = family.steps ?? [];
    this.licenceStep = steps.find((step) => step.stepSelectionType === StepSelectionType.PICK1) ?? this.stepAt(steps, 1);
    this.pick0Steps = steps.filter((step) => step.stepSelectionType === StepSelectionType.PICK0TON);
    this.optionalStep = this.pick0Steps[0] ?? this.stepAt(steps, 2);
  }

  private loadOptionalDeviceTypes() {
    this.optionalOptionsResolved = false;
    const steps = this.pick0Steps.length ? this.pick0Steps : this.optionalStep ? [this.optionalStep] : [];
    const options = steps.flatMap((step) => step.options ?? []);

    if (!options.length) {
      this.resolveOptionalSteps();
      return;
    }

    const hasUnknown = this.hasUnknownOptionTypes(options);
    if (!hasUnknown) {
      this.resolveOptionalSteps();
      return;
    }

    const deviceMatNrs = Array.from(new Set(options.flatMap((option) => option.devices))).map((matNr) => String(matNr));
    if (!deviceMatNrs.length) {
      this.resolveOptionalSteps();
      return;
    }

    this.loadDeviceTypesForOptions(deviceMatNrs, options)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resolveOptionalSteps());
  }

  private resolveOptionalSteps() {
    const buckets: CentralStationOptionalOptionBuckets = resolveOptionalOptionBuckets(
      this.pick0Steps,
      this.optionalStep,
      this.deviceTypesByMatNr,
      this.additionalLicenceTypes,
      this.accessoryTypes,
    );
    this.additionalLicenceOptions = this.filterAvailableOptions(buckets.additionalLicences);
    this.accessoryOptions = this.filterAvailableOptions(buckets.accessories);
    this.optionalOptionsResolved = true;
    if (this.activeStep >= this.steps) this.activeStep = Math.max(0, this.steps - 1);

    this.refreshOverviewData();
  }

  private createCentralStationConfig() {
    const baseOptions = this.getBaseOptions();
    if (!this.family || !this.selectedLicence) return;

    const baseItems = baseOptions.map((option) => MonitoringProductItem.create(option, 1));
    const baseOptionsForConfig = baseItems.length > 1 ? baseItems : undefined;
    const configurationName =
      this.selectedFamilyVariant?.displayName || this.selectedFamily?.displayName || this.family.familyName;

    const centralStationConfig = MonitoringConfigurationFactory.createCentralStation(
      configurationName,
      this.family,
      this.project,
      {
        base: baseItems[0],
        licence: MonitoringProductItem.create(this.selectedLicence.option, 1),
        additionalLicences: this.selectedAdditionalLicences,
        accessories: this.selectedAccessories,
        baseOptions: baseOptionsForConfig,
      },
    );
    this.project.addCentralStations(centralStationConfig);
    this.backButton.navigateBack();
  }

  private updateCentralStationConfig(config: CentralStationConfiguration) {
    const baseOptions = this.getBaseOptions();
    if (!this.family || !this.selectedLicence) return;
    const baseItems = baseOptions.map((option) => MonitoringProductItem.create(option, 1));
    const baseOptionsForConfig = baseItems.length > 1 ? baseItems : undefined;
    const configurationName =
      this.selectedFamilyVariant?.displayName || this.selectedFamily?.displayName || this.family.familyName;

    config.update(
      configurationName,
      this.family.familyNumber,
      baseItems[0],
      MonitoringProductItem.create(this.selectedLicence.option, 1),
      this.selectedAdditionalLicences,
      this.selectedAccessories,
      [],
      baseOptionsForConfig,
    );
    this.backButton.navigateBack();
  }

  private getBaseOptions() {
    const selectedFamilyOption = getSelectedFamilyOption(this.selectedFamily, this.selectedFamilyVariant)?.option;
    if (!selectedFamilyOption?.devices.length) return [];

    return this.filterBaseOptions(this.filterAvailableOptions([selectedFamilyOption]));
  }

  private filterBaseOptions(options: FamilyStepOption[]): FamilyStepOption[] {
    const hasKnownDeviceType = options.some((option) =>
      option.devices.some((device) => this.deviceTypesByMatNr.has(String(device))),
    );
    if (!hasKnownDeviceType) return options;

    return options.filter((option) =>
      option.devices.some((device) => this.deviceTypesByMatNr.get(String(device)) === DeviceType.CS_BASE),
    );
  }

  private filterAvailableOptions(options: FamilyStepOption[]): FamilyStepOption[] {
    const availableOptions: FamilyStepOption[] = [];

    for (const option of options) {
      const devices = option.devices.filter((device) => !this.discontinuedDeviceMatNrs.has(String(device)));
      if (!devices.length) continue;
      if (devices.length === option.devices.length) {
        availableOptions.push(option);
        continue;
      }

      availableOptions.push(
        new FamilyStepOption(
          devices,
          option.groupName,
          option.quantityMin,
          option.quantityMax,
          option.filter1,
          option.filter2,
          option.subType,
          option.groupImageCantoID,
          option.description,
          option.piSheetURL,
          option.configurationURL,
        ),
      );
    }

    return availableOptions;
  }

  private isSelectionValid(options: FamilyStepOption[], selectedItems: MonitoringProductItem[]) {
    if (!options.length) return true;

    const selectedMap = new Map(selectedItems.map((item) => [this.optionKey(item.option), item.quantity]));

    return options.every((option) => {
      const quantity = selectedMap.get(this.optionKey(option)) ?? 0;
      return quantity >= option.quantityMin && quantity <= option.quantityMax;
    });
  }

  private optionKey(option: FamilyStepOption) {
    return getConfigurationOptionIdentifier(this.family?.familyName, option);
  }

  private refreshOverviewData() {
    const items: MonitoringGroupedPartSourceItem[] = buildCentralStationGroupedSourceItems(
      {
        baseOptions: this.getBaseOptions().map((option) => ({ option, quantity: 1 })),
        licence: this.selectedLicence ? { option: this.selectedLicence.option, quantity: 1 } : undefined,
        additionalLicences: this.selectedAdditionalLicences,
        accessories: this.selectedAccessories,
      },
      this.overviewCategoryLabels,
    );

    if (!items.length) {
      this.overviewItems$.next([]);
      return;
    }

    this.overviewItems$.next(items);
  }

  private selectFamily(families: Family[]) {
    if (!families.length) return undefined;
    const targetFamilyNumber = this.originalConfiguration?.familyNumber;
    if (!targetFamilyNumber) return families[0];
    return families.find((family) => family.familyNumber === targetFamilyNumber) ?? families[0];
  }

  private stepAt(steps: FamilyStep[], index: number) {
    return steps.length > index ? steps[index] : undefined;
  }

  private hasUnknownOptionTypes(options: FamilyStepOption[]) {
    return options.some((option) => option.devices.some((device) => !this.deviceTypesByMatNr.has(String(device))));
  }

  private loadDeviceTypesForOptions(deviceMatNrs: string[], options: FamilyStepOption[]) {
    const filterCountry = this.filterCountry ?? "";

    return this.loadAndAppendDevices(deviceMatNrs, options, filterCountry).pipe(
      switchMap((stillMissing) => this.loadFallbackDevices(deviceMatNrs, options, stillMissing)),
      switchMap((stillMissing) => this.refreshDeviceCacheIfNeeded(options, stillMissing)),
      map(() => undefined),
    );
  }

  private loadAndAppendDevices(deviceMatNrs: string[], options: FamilyStepOption[], filterCountry: string) {
    return this.monitoringProductService.loadDevicesFromDB(deviceMatNrs, MonitoringCategory.CENTRAL_STATIONS, filterCountry).pipe(
      map((devices) => {
        appendDeviceTypes(this.deviceTypesByMatNr, devices);
        devices
          .filter((device) => !device.isAvailable)
          .forEach((device) => this.discontinuedDeviceMatNrs.add(String(device.matNr)));
        return this.hasUnknownOptionTypes(options);
      }),
    );
  }

  private loadFallbackDevices(deviceMatNrs: string[], options: FamilyStepOption[], stillMissing: boolean) {
    if (!stillMissing || !this.filterCountry) return of(stillMissing);
    return this.loadAndAppendDevices(deviceMatNrs, options, "");
  }

  private refreshDeviceCacheIfNeeded(options: FamilyStepOption[], stillMissing: boolean) {
    if (!stillMissing || !navigator.onLine || this.hasRefreshedDeviceCache) return of(false);
    this.hasRefreshedDeviceCache = true;
    return this.monitoringProductService.refreshAll().pipe(
      switchMap(() =>
        this.loadWithFallback(
          (country) => this.monitoringProductService.loadAllCategoryDevicesFromDB(MonitoringCategory.CENTRAL_STATIONS, country),
          this.filterCountry,
        ),
      ),
      map((refreshedDevices) => {
        appendDeviceTypes(this.deviceTypesByMatNr, refreshedDevices);
        return this.hasUnknownOptionTypes(options);
      }),
    );
  }
}
